import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Pencil, Check, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { AddressAutocomplete } from '@/components/ui/AddressAutocomplete';
import type { NormalizedAddress } from '@/utils/parseGoogleAddress';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { displayDistanceToKm, kmToDisplayDistance } from '@/utils/locale';

interface Metro {
  id: string;
  name: string;
  is_active: boolean;
  is_unassigned_pool: boolean;
  center_lat: number | null;
  center_lng: number | null;
  radius_km: number | null;
  description: string | null;
  created_at: string;
}

export default function Metros() {
  // Radii are stored in km; the form and table show the tenant's unit (km or miles).
  const { locale } = useCompanyLocale();
  const unit = locale.distanceUnit;
  const kmToDisplay = (km: number): string => kmToDisplayDistance(km, locale).toFixed(1);

  const [isMetroDialogOpen, setIsMetroDialogOpen] = useState(false);
  const [editingMetro, setEditingMetro] = useState<Metro | null>(null);
  const [deleteMetroId, setDeleteMetroId] = useState<string | null>(null);

  // Form state
  const [metroName, setMetroName] = useState('');
  const [addressDisplay, setAddressDisplay] = useState('');
  const [centerLat, setCenterLat] = useState<number | null>(null);
  const [centerLng, setCenterLng] = useState<number | null>(null);
  const [radiusMiles, setRadiusMiles] = useState('');

  const queryClient = useQueryClient();

  const { data: metros = [], isLoading: metrosLoading } = useQuery({
    queryKey: ['metros'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('metros')
        .select('*')
        .eq('is_unassigned_pool', false)
        .order('name');

      if (error) throw error;
      return data as Metro[];
    },
  });

  const createMetroMutation = useMutation({
    mutationFn: async (payload: { name: string; center_lat: number | null; center_lng: number | null; radius_km: number | null; description: string | null }) => {
      const { error } = await supabase
        .from('metros')
        .insert(payload as any);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metros'] });
      toast.success('Metro created successfully');
      setIsMetroDialogOpen(false);
      resetForm();
    },
    onError: (error: Error) => {
      toast.error(`Failed to create metro: ${error.message}`);
    },
  });

  const updateMetroMutation = useMutation({
    mutationFn: async (payload: { id: string; name: string; center_lat: number | null; center_lng: number | null; radius_km: number | null; description: string | null }) => {
      const { id, ...rest } = payload;
      const { error } = await supabase
        .from('metros')
        .update(rest)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metros'] });
      toast.success('Metro updated successfully');
      setIsMetroDialogOpen(false);
      setEditingMetro(null);
      resetForm();
    },
    onError: (error: Error) => {
      toast.error(`Failed to update metro: ${error.message}`);
    },
  });

  const toggleActiveMetroMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase
        .from('metros')
        .update({ is_active })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metros'] });
      toast.success('Metro status updated');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update metro status: ${error.message}`);
    },
  });

  const deleteMetroMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('metros')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metros'] });
      toast.success('Metro deleted successfully');
      setDeleteMetroId(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete metro: ${error.message}`);
    },
  });

  const resetForm = () => {
    setMetroName('');
    setAddressDisplay('');
    setCenterLat(null);
    setCenterLng(null);
    setRadiusMiles('');
  };

  const handleAddressSelected = (normalized: NormalizedAddress) => {
    setAddressDisplay(normalized.formattedAddress);
    setCenterLat(normalized.lat);
    setCenterLng(normalized.lng);
  };

  const handleMetroSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!metroName.trim()) return;

    const payload = {
      name: metroName.trim(),
      center_lat: centerLat,
      center_lng: centerLng,
      radius_km: radiusMiles ? displayDistanceToKm(parseFloat(radiusMiles), locale) : null,
      description: addressDisplay || null,
    };

    if (editingMetro) {
      updateMetroMutation.mutate({ id: editingMetro.id, ...payload });
    } else {
      createMetroMutation.mutate(payload);
    }
  };

  const handleEditMetro = (metro: Metro) => {
    setEditingMetro(metro);
    setMetroName(metro.name);
    setAddressDisplay(metro.description ?? '');
    setCenterLat(metro.center_lat);
    setCenterLng(metro.center_lng);
    setRadiusMiles(metro.radius_km ? kmToDisplay(metro.radius_km) : '');
    setIsMetroDialogOpen(true);
  };

  const handleMetroDialogClose = (open: boolean) => {
    setIsMetroDialogOpen(open);
    if (!open) {
      setEditingMetro(null);
      resetForm();
    }
  };

  if (metrosLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Metros</CardTitle>
              <CardDescription>Manage location metros for filtering leads and jobs</CardDescription>
            </div>
            <Dialog open={isMetroDialogOpen} onOpenChange={handleMetroDialogClose}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Metro
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingMetro ? 'Edit Metro' : 'Add New Metro'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleMetroSubmit} className="space-y-4">
                  <div>
                    <Label>Metro Name</Label>
                    <Input
                      value={metroName}
                      onChange={(e) => setMetroName(e.target.value)}
                      placeholder="e.g., Sydney, Melbourne, Brisbane"
                      required
                    />
                  </div>
                  <div>
                    <Label>Center Address</Label>
                    <AddressAutocomplete
                      value={addressDisplay}
                      onChange={setAddressDisplay}
                      onAddressSelected={handleAddressSelected}
                      placeholder="Search for the metro center address…"
                    />
                    {centerLat && centerLng && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {centerLat.toFixed(5)}, {centerLng.toFixed(5)}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>Radius</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        step="0.1"
                        value={radiusMiles}
                        onChange={(e) => setRadiusMiles(e.target.value)}
                        placeholder="e.g., 30"
                        className="flex-1"
                      />
                      <span className="text-sm text-muted-foreground shrink-0">{unit === 'mi' ? 'miles' : 'km'}</span>
                    </div>
                  </div>
                  <Button type="submit" className="w-full">
                    {editingMetro ? 'Update' : 'Create'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Center Address</TableHead>
                <TableHead>Radius</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {metros.map((metro) => (
                <TableRow key={metro.id}>
                  <TableCell className="font-medium">{metro.name}</TableCell>
                  <TableCell className="max-w-[240px]">
                    {metro.description ? (
                      <span className="text-sm truncate block" title={metro.description}>
                        {metro.description}
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground">No address set</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {metro.radius_km != null ? (
                      <span className="text-sm">{kmToDisplay(metro.radius_km)} {unit}</span>
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={metro.is_active ? 'default' : 'secondary'}>
                      {metro.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEditMetro(metro)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleActiveMetroMutation.mutate({ id: metro.id, is_active: !metro.is_active })}
                      >
                        {metro.is_active ? <X className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteMetroId(metro.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {metros.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">
                    No metros found. Add one to get started.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteMetroId} onOpenChange={() => setDeleteMetroId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Metro</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this metro? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteMetroId && deleteMetroMutation.mutate(deleteMetroId)}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
